import { Link } from "react-router-dom";
import { ArrowUpRight, Download, FileText, Package, Calculator, ExternalLink, Check } from "lucide-react";
import { RevealText, Reveal } from "@/components/site/Reveal";

const HERO_IMG = "https://images.unsplash.com/photo-1583686298564-46fbffda0707?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800";
const INCOTERM_IMG = "https://images.unsplash.com/photo-1632152133952-98b268dc4b86?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const WHY = [
  "Who is responsible for shipping, insurance, and customs duties",
  "Where risk and liability for the goods transfer from seller to buyer",
  "Which party pays for the different stages of transporting goods",
];

const CATEGORIES = [
  { code: "E", title: "E Terms", d: "Seller makes goods available at their premises (EXW – Ex Works)." },
  { code: "F", title: "F Terms", d: "Seller delivers goods to a carrier (FCA – Free Carrier, FAS – Free Alongside Ship, FOB – Free On Board)." },
  { code: "C", title: "C Terms", d: "Seller arranges and pays for transportation but not risk after shipment (CPT, CIP, CFR, CIF)." },
  { code: "D", title: "D Terms", d: "Seller is responsible until goods arrive at the destination (DAP, DPU, DDP)." },
];

const CONTAINERS = [
  { t: "Standard Dry Container", d: "The backbone of international shipping, designed for general cargo such as electronics, clothing, and machinery. Available most commonly in 20ft and 40ft lengths — secure, waterproof, and cost-efficient." },
  { t: "High Cube Container", d: "Offers additional vertical space compared to the standard dry container, maximizing storage for voluminous cargo. Typically one foot taller, available in 20ft and 40ft sizes." },
  { t: "Open Top Container", d: "With a removable roof, open top containers allow for easy crane loading of oversized cargo or bulk items that cannot be loaded through standard doors." },
  { t: "Flat Rack Container", d: "Designed for heavy and oversized cargo, these containers have end walls but no side walls or roof, making them ideal for construction equipment or vehicles." },
  { t: "Refrigerated Container (Reefer)", d: "Equipped with temperature-control machinery for transporting perishable goods, food, or pharmaceuticals. Available in multiple sizes and can be powered by generator sets for long-distance trips." },
  { t: "Tank Container", d: "Specialized containers for liquids, chemicals, and gases. Robust, certified, and often leased from specialized providers." },
  { t: "Insulated Container", d: "Maintains cargo temperature without active refrigeration, suitable for products sensitive to fluctuations but not requiring continual cooling." },
];

export default function Resources() {
  return (
    <div data-testid="resources-page">
      <section className="relative pt-40 pb-20 mx-auto max-w-[1600px] px-5 sm:px-8">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-6">/ Resources</p>
        <h1 className="font-display uppercase tracking-tighter leading-[0.85] text-[13vw] sm:text-[10vw] lg:text-[8vw]">
          <RevealText lines={["Trade,", "decoded."]} testid="page-title" />
        </h1>
        <Reveal delay={0.4}>
          <p className="mt-8 max-w-3xl text-white/70 text-xl leading-relaxed">
            At Freight IQ, we specialize in delivering reliable, streamlined freight services backed by consistent communication every step of the way. Explore our guides, tools, and references to help you ship smarter—with access to dedicated global cargo space tailored for the best combination of route, cost, and transit time.
          </p>
        </Reveal>
      </section>

      <section className="relative h-[55vh] overflow-hidden">
        <img src={HERO_IMG} alt="Colorful stacked shipping containers" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
      </section>

      {/* Incoterms */}
      <section data-testid="incoterms" className="mx-auto max-w-[1600px] px-5 sm:px-8 py-28">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <Reveal className="lg:col-span-4">
            <p className="font-mono text-xs uppercase tracking-widest text-primary mb-4 flex items-center gap-2"><FileText size={14} /> Incoterms</p>
            <h2 className="font-display text-3xl sm:text-4xl uppercase tracking-tighter leading-[0.95] sticky top-28">Shipping Incoterms: the complete guide</h2>
          </Reveal>
          <div className="lg:col-span-8 space-y-10 text-white/60 text-lg leading-relaxed">
            <div>
              <h3 className="font-display text-xl uppercase tracking-tight text-white mb-3">What are Incoterms?</h3>
              <p>Incoterms (International Commercial Terms) are a set of internationally recognized rules published by the International Chamber of Commerce (ICC) that define the responsibilities of sellers and buyers in global trade agreements and shipment transactions. These standardized codes clarify how costs, risks, and obligations are shared between trading partners, helping prevent misunderstandings in international and domestic contracts.</p>
            </div>
            <div>
              <h3 className="font-display text-xl uppercase tracking-tight text-white mb-3">Why use Incoterms?</h3>
              <p className="mb-4">Utilizing Incoterms in your contracts ensures a clear understanding of the following:</p>
              <ul className="space-y-3">
                {WHY.map((w) => (
                  <li key={w} className="flex items-start gap-3 text-white/80"><Check size={16} className="text-primary shrink-0 mt-1.5" /><span>{w}</span></li>
                ))}
              </ul>
              <p className="mt-4">Each term specifies a distinct arrangement, streamlining the process and reducing disputes by providing structure for sales agreements and shipments worldwide.</p>
            </div>
          </div>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {CATEGORIES.map((c, i) => (
            <Reveal key={c.code} delay={(i % 4) * 0.06} className="bg-[#080808] p-8">
              <span className="font-display text-6xl stroke-text block">{c.code}</span>
              <h3 className="font-display text-xl uppercase tracking-tight mt-5">{c.title}</h3>
              <p className="text-white/60 text-sm mt-3">{c.d}</p>
            </Reveal>
          ))}
        </div>

        <div className="mt-12 grid lg:grid-cols-12 gap-8 items-center">
          <Reveal className="lg:col-span-7">
            <h3 className="font-display text-2xl uppercase tracking-tight mb-3">Choosing the right Incoterm</h3>
            <p className="text-white/60 text-lg">Selecting the appropriate Incoterm depends on the type of goods, the modes of transport involved, and your desired level of control or risk. Our team can help you choose terms best suited to your import or export needs, ensuring a seamless trade experience.</p>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/incoterms-guide`} data-testid="incoterm-download" className="inline-flex items-center gap-3 mt-6 hover-fill px-7 py-3.5 font-mono text-xs uppercase tracking-widest">
              Download Incoterms Guide <Download size={14} />
            </a>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5">
            <div className="relative h-56 overflow-hidden border border-white/10">
              <img src={INCOTERM_IMG} alt="International trade documents" className="absolute inset-0 h-full w-full object-cover opacity-75" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Shipping Containers */}
      <section data-testid="containers" className="border-y border-white/10 bg-[#080808]">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 py-28">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-widest text-primary mb-4 flex items-center gap-2"><Package size={14} /> Shipping Containers</p>
            <h2 className="font-display text-4xl sm:text-6xl uppercase tracking-tighter leading-[0.9]">Container dimensions.</h2>
            <p className="text-white/60 text-lg mt-6 max-w-3xl">Shipping containers are available in various types and sizes to suit almost any cargo need. Choosing the right container ensures safe and efficient transportation of your goods.</p>
          </Reveal>

          <div className="mt-14 grid md:grid-cols-2 gap-px bg-white/10 border border-white/10">
            {CONTAINERS.map((c, i) => (
              <Reveal key={c.t} delay={(i % 2) * 0.06} className="bg-[#080808] p-8">
                <div className="flex items-baseline gap-4">
                  <span className="font-mono text-xs text-primary">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-display text-xl uppercase tracking-tight">{c.t}</h3>
                </div>
                <p className="text-white/60 mt-3">{c.d}</p>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/container-guide`} data-testid="container-download" className="inline-flex items-center gap-3 mt-12 hover-fill px-7 py-3.5 font-mono text-xs uppercase tracking-widest">
              Download Container Guide <Download size={14} />
            </a>
          </Reveal>
        </div>
      </section>

      {/* Volume Calculator */}
      <section data-testid="volume-calculator" className="mx-auto max-w-[1600px] px-5 sm:px-8 py-28">
        <div className="border border-white/10 p-10 sm:p-16 flex flex-col lg:flex-row lg:items-center justify-between gap-10">
          <div className="max-w-2xl">
            <p className="font-mono text-xs uppercase tracking-widest text-primary mb-4 flex items-center gap-2"><Calculator size={14} /> Shipping Volume Calculator</p>
            <h2 className="font-display text-3xl sm:text-5xl uppercase tracking-tighter">Calculate your CBM in seconds.</h2>
            <p className="text-white/60 text-lg mt-5">Calculating the volume of your shipment is essential for estimating shipping costs, optimizing cargo space, and ensuring your goods fit within container or transport specifications. Our recommended calculator is a free, user-friendly online tool that determines the cubic volume and volumetric weight of your packages in just a few clicks.</p>
          </div>
          <a href="https://www.cbmcalculator.com/" target="_blank" rel="noopener noreferrer" data-testid="calculator-link" className="inline-flex items-center gap-3 hover-fill px-8 py-4 font-mono text-xs uppercase tracking-widest w-fit shrink-0">
            Open Calculator <ExternalLink size={16} />
          </a>
        </div>
      </section>

      <section className="border-t border-white/10 bg-[#080808]">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 py-24 flex flex-col sm:flex-row sm:items-center justify-between gap-8">
          <h2 className="font-display text-3xl sm:text-5xl uppercase tracking-tighter max-w-2xl">Need help applying these to your shipment?</h2>
          <a href="mailto:sales@freight-iq.com?subject=New%20Enquiry%20-%20Freight%20IQ" data-testid="resources-cta" className="inline-flex items-center gap-3 hover-fill px-8 py-4 font-mono text-xs uppercase tracking-widest w-fit shrink-0">
            Connect with us <ArrowUpRight size={16} />
          </a>
        </div>
      </section>
    </div>
  );
}
